// Per-sub-field animation wrapper for atomic renderers. `playing===undefined`
// keeps children static (editor mode); a numeric `currentTimeSec` drives the
// animation deterministically (timeline scrub / recorder), otherwise wall-clock.
// Type Writer / Wavy Text route through SpecialTextWrappers so VisorBox's
// box-level path can reuse the same components.

import React, { useContext } from "react";
import { AnimatedWrapper } from "../../../common/animation/AnimatedWrapper";
import {
  TypeWriterWrap,
  WavyWrap,
  TYPE_WRITER_NAMES,
  WAVY_NAMES,
} from "../../../common/animation/SpecialTextWrappers";
import { getAnimationTargets } from "../../../common/animation/presets.es6";

/* eslint-disable react/prop-types */

export const AtomicAnimationTimeContext = React.createContext(null);

function resolveName(animSpec) {
  if (!animSpec || !animSpec.animation) return null;
  return typeof animSpec.animation === "string" ? animSpec.animation : animSpec.animation?.name;
}

function resolveSpec(animSpec, type) {
  const name = resolveName(animSpec);
  if (!name) return null;
  const targets = getAnimationTargets(name, type);
  if (!targets) return null;
  return {
    name,
    type,
    initial: targets.initial,
    animateTarget: targets.animate,
    transition: {
      ...(targets.transition || {}),
      duration:
        typeof animSpec.duration === "number"
          ? animSpec.duration
          : (targets.transition?.duration ?? 1),
      delay: typeof animSpec.delay === "number" ? animSpec.delay : 0,
    },
  };
}

function renderLayer(spec, isPaused, seekTimeSec, animationKey, inner, liveClock) {
  if (!spec) return inner;
  if (TYPE_WRITER_NAMES.has(spec.name)) {
    return (
      <TypeWriterWrap
        duration={spec.transition.duration}
        delay={spec.transition.delay}
        isPaused={isPaused}
        seekTimeSec={seekTimeSec}
        timeOriginSec={liveClock?.timeOriginSec}
        sceneId={liveClock?.sceneId}
        reverse={spec.type === "exit"}
      >
        {inner}
      </TypeWriterWrap>
    );
  }
  if (WAVY_NAMES.has(spec.name)) {
    return (
      <WavyWrap
        duration={spec.transition.duration}
        delay={spec.transition.delay}
        isPaused={isPaused}
        seekTimeSec={seekTimeSec}
        timeOriginSec={liveClock?.timeOriginSec}
        sceneId={liveClock?.sceneId}
        reverse={spec.type === "exit"}
        animationKey={animationKey}
      >
        {inner}
      </WavyWrap>
    );
  }
  return (
    <AnimatedWrapper
      initial={spec.initial}
      animateTarget={spec.animateTarget}
      transition={spec.transition}
      isPaused={isPaused}
      seekTimeSec={seekTimeSec}
      timeOriginSec={liveClock?.timeOriginSec}
      sceneId={liveClock?.sceneId}
    >
      {inner}
    </AnimatedWrapper>
  );
}

export function AnimatedField({
  boxAnimation,
  boxExitAnimation,
  playing,
  isPaused = false,
  currentTimeSec = null,
  children,
}) {
  const liveClock = useContext(AtomicAnimationTimeContext);
  if (playing === undefined) return <>{children}</>;

  const entrySpec = resolveSpec(boxAnimation, "entry");
  const exitSpec = resolveSpec(boxExitAnimation, "exit");

  if (!entrySpec && !exitSpec) return <>{children}</>;

  const seekTimeSec = currentTimeSec;
  const animationKey = `${entrySpec?.name || ""}::${exitSpec?.name || ""}::${
    seekTimeSec == null ? "live" : "scrub"
  }`;

  let content = children;
  content = renderLayer(entrySpec, isPaused, seekTimeSec, `${animationKey}-in`, content, liveClock);
  content = renderLayer(exitSpec, isPaused, seekTimeSec, `${animationKey}-out`, content, liveClock);
  return content;
}

export default AnimatedField;
